/**
 * Item Set Service - Frontend service for item set operations
 * Provides methods to fetch items from Lead model by itemSetId
 */

import api from "../api.jsx";
import { logError, logInfo, logWarn } from "../utils/logger.js";

export class ItemSetService {
  /**
   * Get items by itemSetId
   * @param {string} itemSetId - Item set ID
   * @returns {Promise<Array>} Items array from Lead
   */
  static async getItemsBySetId(itemSetId) {
    if (!itemSetId) {
      logWarn("ItemSetService: itemSetId is missing", { itemSetId });
      return [];
    }

    try {
      const response = await api.get(`/item-sets/${itemSetId}/items`);

      if (response.success && response.data) {
        logInfo("ItemSetService: Items fetched successfully", {
          itemSetId,
          itemCount: response.data.length,
        });
        return response.data;
      }

      logWarn("ItemSetService: Invalid response format", {
        itemSetId,
        response,
      });
      return [];
    } catch (error) {
      logError("ItemSetService: Failed to fetch items by itemSetId", {
        itemSetId,
        error: error.message,
        status: error.response?.status || error.status,
      });
      return [];
    }
  }

  /**
   * Populate items for a single entity
   * @param {Object} entity - Entity with itemSetId
   * @returns {Promise<Object>} Entity with populated items
   */
  static async populateItems(entity) {
    if (!entity || !entity.itemSetId) {
      // Fallback to existing items array for backward compatibility
      return {
        ...entity,
        items: entity.items || [],
      };
    }

    try {
      const items = await this.getItemsBySetId(entity.itemSetId);
      return {
        ...entity,
        items: items,
      };
    } catch (error) {
      logWarn("ItemSetService: Failed to populate items, using fallback", {
        itemSetId: entity.itemSetId,
        error: error.message,
      });
      // Fallback to existing items array
      return {
        ...entity,
        items: entity.items || [],
      };
    }
  }

  /**
   * Populate items for multiple entities (batch operation)
   * @param {Array} entities - Array of entities with itemSetId
   * @returns {Promise<Array>} Entities with populated items
   */
  static async populateItemsBatch(entities) {
    if (!Array.isArray(entities) || entities.length === 0) {
      return [];
    }

    try {
      // Extract unique itemSetIds
      const itemSetIds = [
        ...new Set(
          entities
            .map((e) => e.itemSetId)
            .filter((id) => id && id.trim() !== "")
        ),
      ];

      if (itemSetIds.length === 0) {
        // No itemSetIds found, return entities with their existing items
        return entities.map((entity) => ({
          ...entity,
          items: entity.items || [],
        }));
      }

      // Fetch all items in parallel
      const itemsPromises = itemSetIds.map((id) =>
        this.getItemsBySetId(id).then((items) => ({ id, items }))
      );

      const itemsResults = await Promise.all(itemsPromises);

      // Create map of itemSetId -> items
      const itemsMap = new Map();
      itemsResults.forEach(({ id, items }) => {
        itemsMap.set(id, items);
      });

      // Populate items for each entity
      const populatedEntities = entities.map((entity) => {
        if (entity.itemSetId && itemsMap.has(entity.itemSetId)) {
          return {
            ...entity,
            items: itemsMap.get(entity.itemSetId),
          };
        } else {
          // Fallback to existing items array for backward compatibility
          return {
            ...entity,
            items: entity.items || [],
          };
        }
      });

      logInfo("ItemSetService: Batch items populated successfully", {
        entityCount: entities.length,
        itemSetIdsFound: itemSetIds.length,
      });

      return populatedEntities;
    } catch (error) {
      logError("ItemSetService: Failed to populate items batch", {
        entityCount: entities.length,
        error: error.message,
      });
      // Fallback: return entities with their existing items
      return entities.map((entity) => ({
        ...entity,
        items: entity.items || [],
      }));
    }
  }
}

